/**
 * MCP Write Query Approval Dialog
 *
 * Shows a confirmation dialog when an AI agent requests to execute a write query
 * and the connection's permission level is set to 'confirm'.
 */

import { ShieldAlert, Database, Bot, Clock } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import type { McpWriteApprovalRequest, WriteQueryType } from '../../../types/mcpUiControl';
import { useReturnFocus } from './useReturnFocus';

interface McpWriteApprovalDialogProps {
  request: McpWriteApprovalRequest | null;
  onApprove: (requestId: string) => void;
  onDeny: (requestId: string) => void;
}

/**
 * Get variant color for query type badge
 */
function getQueryTypeBadgeVariant(queryType: WriteQueryType): 'default' | 'destructive' | 'secondary' {
  switch (queryType) {
    case 'DELETE':
    case 'DROP':
    case 'TRUNCATE':
      return 'destructive';
    case 'UPDATE':
    case 'ALTER':
      return 'default';
    default:
      return 'secondary';
  }
}

/**
 * Get human-readable description for query type
 */
function getQueryTypeDescription(queryType: WriteQueryType): string {
  switch (queryType) {
    case 'INSERT':
      return 'This query will add new data to the database.';
    case 'UPDATE':
      return 'This query will modify existing data in the database.';
    case 'DELETE':
      return 'This query will permanently remove data from the database.';
    case 'CREATE':
      return 'This query will create a new database object.';
    case 'ALTER':
      return 'This query will modify the structure of a database object.';
    case 'DROP':
      return 'This query will permanently remove a database object.';
    case 'TRUNCATE':
      return 'This query will remove all data from a table.';
    case 'MERGE':
      return 'This query will insert, update, or delete data based on conditions.';
    case 'EXEC':
      return 'This query will execute a stored procedure that may modify data.';
    default:
      return 'This query may modify database data or structure.';
  }
}

/**
 * Format SQL for preview (truncate long queries)
 */
function formatSqlPreview(sql: string, maxLength = 500): string {
  const normalized = sql.replace(/\s+/g, ' ').trim();
  if (normalized.length <= maxLength) {
    return normalized;
  }
  return normalized.slice(0, maxLength) + '...';
}

export function McpWriteApprovalDialog({
  request,
  onApprove,
  onDeny,
}: McpWriteApprovalDialogProps) {
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  useReturnFocus(request?.requestId ?? null);

  // Set up countdown timer
  useEffect(() => {
    if (!request) {
      setTimeRemaining(null);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      return;
    }

    const timeoutMs = request.timeoutMs ?? 60000;
    const startTime = Date.now();
    const endTime = startTime + timeoutMs;

    // Initial time remaining
    setTimeRemaining(Math.ceil((endTime - Date.now()) / 1000));

    // Update every second
    timerRef.current = setInterval(() => {
      const remaining = Math.ceil((endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        setTimeRemaining(0);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
      } else {
        setTimeRemaining(remaining);
      }
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [request]);

  if (!request) {
    return null;
  }

  const handleApprove = () => {
    onApprove(request.requestId);
  };

  const handleDeny = () => {
    onDeny(request.requestId);
  };

  const queryTypeDescription = getQueryTypeDescription(request.queryType);
  const sqlPreview = formatSqlPreview(request.sql);
  const badgeVariant = getQueryTypeBadgeVariant(request.queryType);

  return (
    <AlertDialog open={!!request} onOpenChange={(open) => !open && handleDeny()}>
      <AlertDialogContent className="max-w-lg" data-testid="mcp-write-approval-dialog">
        <AlertDialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning/10">
              <Bot className="h-5 w-5 text-warning" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              {/* Round B A20 parity with the storage approval: the question first, "Requested by AI" as detail. */}
              <AlertDialogTitle className="text-lg" data-testid="mcp-write-approval-heading">
                Run this {request.queryType} query?
              </AlertDialogTitle>
              <AlertDialogDescription className="mt-1">
                Requested by AI · a write query needs your approval
              </AlertDialogDescription>
            </div>
          </div>
        </AlertDialogHeader>

        <div className="space-y-4 py-2">
          {/* Connection & Query Type Info */}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <div className="flex items-center gap-1.5 text-muted-foreground">
              <Database className="h-3.5 w-3.5" />
              <span>
                {request.connectionName || request.connectionId}
                {request.database && ` / ${request.database}`}
              </span>
            </div>
            <Badge variant={badgeVariant}>{request.queryType}</Badge>
          </div>

          {/* Description */}
          <p className="text-sm text-foreground leading-relaxed">
            {queryTypeDescription}
          </p>

          {/* SQL Preview */}
          <div className="rounded-md border border-border bg-muted/50 p-3">
            <div className="flex items-start gap-2">
              <ShieldAlert className="h-4 w-4 mt-0.5 flex-shrink-0 text-warning" aria-hidden />
              <code
                className="text-xs text-muted-foreground font-mono break-words whitespace-pre-wrap"
                data-testid="mcp-write-approval-sql"
              >
                {sqlPreview}
              </code>
            </div>
          </div>

          {/* Classification reason if provided */}
          {request.classificationReason && (
            <p className="text-xs text-muted-foreground">
              <strong>Note:</strong> {request.classificationReason}
            </p>
          )}

          {/* Timeout warning */}
          {timeRemaining !== null && timeRemaining <= 30 && (
            <div className="flex items-center gap-2 text-xs text-warning">
              <Clock className="h-3.5 w-3.5" />
              <span>
                {timeRemaining > 0
                  ? `Request expires in ${timeRemaining}s (will be denied)`
                  : 'Request expired'}
              </span>
            </div>
          )}
        </div>

        <AlertDialogFooter className="gap-2 sm:gap-0">
          {/* Round B A14 (parity): Deny is the AlertDialog's Cancel — Radix's initial focus; it closes through
              onOpenChange → handleDeny, so it never denies twice. */}
          <AlertDialogCancel data-testid="mcp-write-approval-deny">
            Deny
          </AlertDialogCancel>
          <Button
            variant={badgeVariant === 'destructive' ? 'destructive' : 'default'}
            onClick={handleApprove}
            data-testid="mcp-write-approval-approve"
            disabled={timeRemaining === 0}
          >
            Approve & Execute
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
